/**
 * The redline product's server state: the project's materialized
 * findings, the edit-op mutation (drag -> POST -> instant verdict),
 * and the accept/reject ratifications. Every mutation invalidates
 * elements + findings -- the canvas and the panel re-render from
 * server truth, never from optimistic guesses.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { API_BASE, authFetch } from '../../api';

export interface FixSuggestion {
  kind: string;
  description: string;
  params: Record<string, number>;
}

export interface StoredFinding {
  id: number;
  rule_name: string;
  origin_id: string;
  element_name: string | null;
  measured: Record<string, unknown>;
  required: Record<string, unknown>;
  suggestion: FixSuggestion | null;
  state: 'open' | 'accepted' | 'rejected';
}

export function useFindings(projectId: string) {
  return useQuery<StoredFinding[]>({
    queryKey: ['findings', projectId],
    queryFn: async () => {
      const res = await authFetch(
        `${API_BASE}/v1/compliance/findings?project_id=${encodeURIComponent(projectId)}`,
      );
      if (!res.ok) throw new Error(`findings: ${res.status}`);
      return (await res.json()) ?? [];
    },
  });
}

/** The latest run's counts summed across rules -- the affirmative
 * claim (in-compliance is a first-class assertion, not silence). */
export interface RunReportTotals {
  compliant: number;
  breach: number;
}

export function useComplianceReport(projectId: string) {
  return useQuery<RunReportTotals | null>({
    queryKey: ['compliance-report', projectId],
    queryFn: async () => {
      const res = await authFetch(
        `${API_BASE}/v1/compliance/report?project_id=${encodeURIComponent(projectId)}`,
      );
      if (res.status === 404) return null;
      if (!res.ok) throw new Error(`compliance report: ${res.status}`);
      const rules: RunReportTotals[] = (await res.json()) ?? [];
      if (rules.length === 0) return null;
      return rules.reduce(
        (sum, rule) => ({
          compliant: sum.compliant + rule.compliant,
          breach: sum.breach + rule.breach,
        }),
        { compliant: 0, breach: 0 },
      );
    },
  });
}

function useInvalidateRedline(projectId: string) {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ['elements', projectId] });
    void queryClient.invalidateQueries({ queryKey: ['findings', projectId] });
    void queryClient.invalidateQueries({ queryKey: ['compliance-report', projectId] });
  };
}

export function useApplyEdit(projectId: string) {
  const invalidate = useInvalidateRedline(projectId);
  return useMutation({
    mutationFn: async (edit: { originId: string; op: string; params: Record<string, number> }) => {
      const started = performance.now();
      const res = await authFetch(`${API_BASE}/v1/edits/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          project_id: projectId,
          origin_id: edit.originId,
          op: edit.op,
          params: edit.params,
        }),
      });
      if (!res.ok) throw new Error(`edit ${edit.op}: ${res.status}`);
      // Measured performance is a feature (APP_SPEC): the verdict's
      // real round-trip, measured here, shown quietly in the inspector.
      return { ...(await res.json()), round_trip_ms: Math.round(performance.now() - started) };
    },
    onSettled: invalidate,
  });
}

export function useFixAll(projectId: string) {
  const invalidate = useInvalidateRedline(projectId);
  return useMutation({
    // originIds set = the FILTERED fix-all (APP_SPEC): the fixpoint
    // narrows to exactly the elements the caller is looking at.
    mutationFn: async (originIds?: string[]) => {
      const res = await authFetch(`${API_BASE}/v1/compliance/fix-all`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          project_id: projectId,
          refresh: true,
          origin_ids: originIds ?? [],
        }),
      });
      if (!res.ok) throw new Error(`fix-all: ${res.status}`);
      return res.json();
    },
    // No polling here: the worker's FINDINGS/FIX_ALL stream events
    // invalidate as each round materializes (see api/stream.ts) --
    // the halos clear live. Invalidate once for the 202 itself.
    onSettled: invalidate,
  });
}

const EXPORT_POLL_MS = 4000;
const EXPORT_POLL_ATTEMPTS = 30; // exports re-parse the original file: ~tens of seconds

export function useExportIfc(projectId: string) {
  return useMutation({
    mutationFn: async () => {
      // refresh: the engineer exports what they see NOW, never a
      // stale materialization from before their edits.
      const post = (refresh: boolean) =>
        authFetch(`${API_BASE}/v1/exports/ifc`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ project_id: projectId, refresh }),
        });
      // A prior export may already be materialized; polls answer 200
      // with it while the fresh one runs. Note its name and wait for a
      // DIFFERENT file -- the one carrying the current geometry.
      const prior = await post(false);
      const priorFile: string | null =
        prior.status === 200 ? (await prior.json()).file_name : null;
      let res = await post(true);
      let result: { file_name: string } | null = null;
      for (let attempt = 0; attempt < EXPORT_POLL_ATTEMPTS; attempt++) {
        await new Promise((resolve) => setTimeout(resolve, EXPORT_POLL_MS));
        res = await post(false);
        if (res.status === 200) {
          const candidate: { file_name: string } = await res.json();
          if (candidate.file_name !== priorFile) {
            result = candidate;
            break;
          }
        } else if (res.status !== 202) {
          throw new Error(`export: ${res.status}`);
        }
      }
      if (result === null) throw new Error('export: timed out waiting for the fresh file');

      const file = await authFetch(`${API_BASE}/v1/exports/ifc/${result.file_name}`);
      if (!file.ok) throw new Error(`export download: ${file.status}`);
      const url = URL.createObjectURL(await file.blob());
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = result.file_name;
      anchor.click();
      URL.revokeObjectURL(url);
      return result;
    },
  });
}

export function useDecideFinding(projectId: string) {
  const invalidate = useInvalidateRedline(projectId);
  return useMutation({
    mutationFn: async (decision: { findingId: number; accept: boolean; apply?: boolean }) => {
      const action = decision.accept ? 'accept' : 'reject';
      const res = await authFetch(
        `${API_BASE}/v1/compliance/findings/${decision.findingId}/${action}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: decision.accept ? JSON.stringify({ apply: decision.apply ?? true }) : undefined,
        },
      );
      if (!res.ok) throw new Error(`finding ${action}: ${res.status}`);
      return res.json();
    },
    onSettled: invalidate,
  });
}
